import { useQuery } from "@apollo/client";
import { Button, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement } from "react";

import { ListCampaignRegistrationsQuery } from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { ButtonLink } from "~frontend/components/Link";
import {
  DELETE_TEN_DLC_CAMPAIGN_REGISTRATION,
  LIST_CAMPAIGN_REGISTRATIONS,
} from "~frontend/graphql/tenDlcRegistrations";
import {
  useCurrentUserIsStaff,
  useFeatureFlag,
} from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

const RegistrationStatusBox = ({
  campaignRegistrationId,
  registrationSubmittedAt,
  campaignCreatedAt,
  state,
  organizationName,
  createdBy,
  submittedBy,
  onDelete,
}: {
  campaignRegistrationId: string;
  registrationSubmittedAt?: string;
  campaignCreatedAt?: string;
  state: "DRAFT" | "PENDING" | "PROVISIONING";
  organizationName?: string | null;
  createdBy?: { fullName: string } | null;
  submittedBy?: { fullName: string } | null;
  onDelete: () => void;
}): ReactElement => {
  const { subaccountLink } = useSubaccount()!;
  const confirm = useConfirm();
  const isStaff = useCurrentUserIsStaff();

  let stage;
  const buttonLabel =
    state === "DRAFT" ? "Review and Edit Draft" : "Review Submission";

  const handleDelete = async () => {
    const { confirmed } = await confirm({
      title: "Delete 10DLC Campaign Registration",
      description: `Are you sure you want to delete the 10DLC campaign registration${organizationName ? ` for ${organizationName}` : ""}?`,
    });
    if (confirmed) {
      onDelete();
    }
  };

  if (state === "DRAFT") {
    stage = "Draft";
  } else if (state === "PENDING") {
    stage = "Under review";
  } else if (state === "PROVISIONING") {
    stage = "Pending";
  }

  return (
    <FlexBox
      sx={{
        p: 3,
        color: state === "DRAFT" ? palette.darkGray : palette.navy,
        borderRadius: 0.5,
        border: state === "DRAFT" ? "1px solid" : undefined,
        borderColor: palette.midGray,
        backgroundColor: state !== "DRAFT" ? palette.lightOrange : undefined,
      }}
      justifyContent="space-between"
      alignItems="flex-end"
    >
      <FlexBox direction="column" gap={0.5}>
        <Typography variant="body2Bold" component="div">
          10DLC Campaign Submission
          {organizationName ? ` for ${organizationName}` : ""}: {stage}
        </Typography>
        {registrationSubmittedAt ? (
          <Typography variant="body2" component="div">
            <strong>Submitted:</strong>{" "}
            {new Date(registrationSubmittedAt).toLocaleDateString()}{" "}
            {submittedBy && `by ${submittedBy.fullName}`}
          </Typography>
        ) : (
          <div />
        )}
        {campaignCreatedAt && (
          <Typography variant="body2" component="div">
            <strong>Created:</strong>{" "}
            {new Date(campaignCreatedAt).toLocaleDateString()}{" "}
            {createdBy && `by ${createdBy.fullName}`}
          </Typography>
        )}
        {registrationSubmittedAt && (
          <Typography
            variant="body2"
            component="div"
            sx={{ maxWidth: "700px" }}
          >
            Your 10DLC registration is under review. Approval typically takes
            7-10 business days (excluding weekends), but it may take longer if
            additional verification is needed. Questions? Reach out to{" "}
            <a href="mailto:compliance@scaletowin.com">
              compliance@scaletowin.com
            </a>
            .
          </Typography>
        )}
      </FlexBox>
      <FlexBox direction="column" alignItems="flex-end" gap={1}>
        <ButtonLink
          sx={{
            border: "1px solid",
            borderColor: state === "DRAFT" ? palette.darkGray : palette.navy,
          }}
          to={subaccountLink(`/10dlc/registration/${campaignRegistrationId}`)}
        >
          <Typography
            variant="h4Caps"
            component="div"
            sx={{ color: state === "DRAFT" ? palette.darkGray : palette.navy }}
          >
            {buttonLabel}
          </Typography>
        </ButtonLink>
        {isStaff && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleDelete}
          >
            <Typography variant="h4Caps" component="div">
              Delete
            </Typography>
          </Button>
        )}
      </FlexBox>
    </FlexBox>
  );
};

export const InProgressRegistrations = (): ReactElement => {
  const { subaccount } = useSubaccount()!;

  const tenDlcRegistrationInApp = useFeatureFlag("tenDlcRegistrationInApp");
  const { data, loading, error, refetch } =
    useQuery<ListCampaignRegistrationsQuery>(LIST_CAMPAIGN_REGISTRATIONS, {
      variables: {
        input: {
          subaccountId: subaccount.id,
        },
      },
      skip: !subaccount || !tenDlcRegistrationInApp,
    });

  const [deleteTenDlcCampaignRegistration] = useMutation(
    DELETE_TEN_DLC_CAMPAIGN_REGISTRATION,
    {
      onErrorSnackbarMessage: "Failed to delete 10DLC campaign registration",
    }
  );

  const handleDelete = async (campaignRegistrationId: string) => {
    const result = await deleteTenDlcCampaignRegistration({
      variables: {
        input: { campaignRegistrationId },
      },
    });

    if (!result.errors) {
      void refetch();
    }
  };

  if (!subaccount || !tenDlcRegistrationInApp) {
    return <></>;
  }

  return (
    <AsyncWrapper loading={loading} errors={error} data={data}>
      {({ listCampaignRegistrations }) => (
        <FlexBox direction="column" gap={1}>
          {listCampaignRegistrations.map(registration => {
            const displayName =
              registration.brandRegistration.displayName ||
              registration.brandRegistration.organizationName;

            const baseParams = {
              organizationName: displayName,
              campaignRegistrationId: registration.id,
              registrationSubmittedAt: registration.submittedAt,
              campaignCreatedAt: registration.createdAt,
              createdBy: registration.createdByUser,
              submittedBy: registration.submittedByUser,
              onDelete: () => handleDelete(registration.id),
            };

            if (!registration.submittedAt) {
              return <RegistrationStatusBox {...baseParams} state="DRAFT" />;
            } else if (!registration.tenDlcCampaign) {
              return <RegistrationStatusBox {...baseParams} state="PENDING" />;
            } else if (!registration.tenDlcCampaign.readyEmailSentAt) {
              return (
                <RegistrationStatusBox
                  {...baseParams}
                  campaignCreatedAt={registration.tenDlcCampaign.createdAt}
                  state="PROVISIONING"
                />
              );
            }
          })}
        </FlexBox>
      )}
    </AsyncWrapper>
  );
};
